import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CheckCircle } from '@mui/icons-material';
import {
	Main,
	RiskButton,
	StatsCard,
	StyledCardInner,
} from '../styles/commonStyles';
import { Header } from './Header';
import { WithdrawButton } from '../../NewDashboard/components/WithdrawButton';
import { formatUSDC, getUSDCBalance } from 'core/vaultTransactions';
import { PortfolioResponse, User } from 'api/client';
import { AGENTS } from 'data/constant';
import { AgentDetail, AGENTS_LIST } from 'data/Agents';
import { VaultConfig } from 'core/constants';

interface ActiveDashboardViewProps {
	balance: number;
	totalYield: number;
	activeAccount: any;
	loadingPosition: boolean;
	positionData: any;
	lastPositionId: number | null | undefined;
	walletAddress: string;
	setUsdcBalance: (balance: number) => void;
	onLogout: () => void;
	aptosBalance: number;
	vaultBalance: number;
	userInfo?: User;
	userApyValue: PortfolioResponse | null;
	agentsList: typeof AGENTS_LIST;
	selectedAgentId: number | null;
	apiBaseUrl: string;
	vaultConfig?: Partial<VaultConfig>;
	onSelectAgent: (id: number) => void;
}

export const ActiveDashboardView: React.FC<ActiveDashboardViewProps> = ({
	balance,
	activeAccount,
	loadingPosition,
	positionData,
	lastPositionId,
	setUsdcBalance,
	totalYield,
	vaultBalance,
	userInfo,
	userApyValue,
	agentsList,
	selectedAgentId,
	apiBaseUrl,
	vaultConfig,
	onSelectAgent,
}) => {
	const [activityDetailOpen, setActivityDetailOpen] = useState(false);

	const positionSelected = userApyValue?.positions.find(
		(d) => d?.posId === positionData?.id,
	);

	const activeAgent =
		agentsList.find((agent) => agent.id === selectedAgentId) ||
		agentsList[0];

	const userAmount = parseInt(userInfo?.lastDeposit?.amountBase || '0');
	const positionAmount = parseInt(positionData?.result?.[1] || '0');
	const finalized = !!positionData
		? JSON.parse(positionData?.result?.[3])
		: false;

	const isAllocated = userAmount !== positionAmount && !finalized;

	const isHyperionAgent =
		activeAgent?.details
			.map((d) => {
				return d.name;
			})
			.toString() === 'Hyperion';

	const formatDays = (value?: number | null) =>
		typeof value === 'number' && value >= 0
			? `${value} ${value < 2 ? 'day' : 'days'}`
			: '—';

	const renderBreakdown = (breakdown?: Record<string, string>) => {
		if (!breakdown || Object.keys(breakdown).length === 0) {
			return (
				<Typography variant="body2" color="textSecondary">
					No unrealized breakdown available.
				</Typography>
			);
		}
		return (
			<Box
				display="grid"
				gridTemplateColumns="repeat(auto-fit,minmax(190px,1fr))"
				gap={1}
			>
				{Object.entries(breakdown).map(([key, value]) => (
					<Box key={key}>
						<Typography variant="caption" color="textSecondary">
							{key === 'mekleUSDCValue'
								? 'Decibel Position'
								: activeAgent?.agentDetails[key] ?? key}
						</Typography>
						<Typography variant="body2" fontWeight={500}>
							{formatUSDC(parseInt(value))} USDC
						</Typography>
					</Box>
				))}
			</Box>
		);
	};

	return (
		<Main>
			<Header />

			<Box
				maxWidth="1200px"
				width="100%"
				p={{ xs: 1.5, sm: 2, md: 3 }}
				mt={{ xs: 1, sm: 2 }}
			>
				<Box
					display="flex"
					flexDirection={{ xs: 'column', sm: 'row' }}
					justifyContent="space-between"
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					mb={{ xs: 1.5, sm: 2 }}
					gap={{ xs: 1.5, sm: 0 }}
				>
					<Box
						display="flex"
						alignItems="center"
						gap={{ xs: 1, sm: 2 }}
						flexWrap="wrap"
					>
						<Chip
							label={activeAgent?.name || 'Selected Agent'}
							color="primary"
							sx={{
								color: 'white',
								fontSize: {
									xs: '12px',
									sm: '14px',
									md: '15px',
								},
								fontWeight: '600',
								height: { xs: '28px', sm: '32px' },
							}}
						/>

						{Boolean(userInfo?.lastDeposit?.amountBase) &&
							positionData?.result?.[1] === '0' && (
								<Chip
									icon={<CheckCircle />}
									label="Earning"
									color="success"
									sx={{
										color: 'white',
										fontSize: {
											xs: '12px',
											sm: '14px',
											md: '15px',
										},
										fontWeight: '600',
										height: { xs: '28px', sm: '32px' },
									}}
								/>
							)}
					</Box>
				</Box>

				{/* Stats Cards */}
				<Grid container spacing={{ xs: 1.5, sm: 2 }} mb={2}>
					<Grid item xs={12} sm={4}>
						<StatsCard>
							<StyledCardInner>
								<CardContent
									sx={{
										padding: { xs: '20px', sm: '24px' },
										paddingBottom: {
											xs: '20px !important',
											sm: '24px !important',
										},
									}}
								>
									<Typography
										variant="subtitle2"
										color="textSecondary"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									>
										Balance
									</Typography>
									<Typography
										variant="h4"
										color="textPrimary"
										fontFamily="Oxanium"
										fontWeight={500}
										sx={{
											fontSize: {
												xs: '24px',
												sm: '30px',
												md: '34px',
											},
										}}
									>
										${balance.toLocaleString()}
									</Typography>
									<Typography
										variant="body2"
										color="textSecondary"
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									>
										USDC deposited
									</Typography>
								</CardContent>
							</StyledCardInner>
						</StatsCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<StatsCard>
							<StyledCardInner>
								<CardContent
									sx={{
										padding: { xs: '20px', sm: '24px' },
										paddingBottom: {
											xs: '20px !important',
											sm: '24px !important',
										},
									}}
								>
									<Typography
										variant="subtitle2"
										color="textSecondary"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									>
										Total Yield
									</Typography>
									<Typography
										variant="h4"
										color="success.main"
										fontFamily="Oxanium"
										fontWeight={500}
										sx={{
											fontSize: {
												xs: '24px',
												sm: '30px',
												md: '34px',
											},
										}}
									>
										{(positionSelected?.daysSinceAllocation ||
											0) > 0.5
											? (() => {
													const yieldValue =
														parseFloat(
															`${totalYield}`,
														) || 0;
													const displayValue =
														yieldValue > 50
															? '50+'
															: yieldValue < 0
															? '0'
															: yieldValue.toFixed(
																	2,
															  );
													return `${displayValue}% APY`;
											  })()
											: '-'}
									</Typography>
									<Typography
										variant="body2"
										color="textSecondary"
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									/>
								</CardContent>
							</StyledCardInner>
						</StatsCard>
					</Grid>

					<Grid item xs={12} sm={4}>
						<StatsCard>
							<StyledCardInner>
								<CardContent
									sx={{
										padding: { xs: '20px', sm: '24px' },
										paddingBottom: {
											xs: '20px !important',
											sm: '24px !important',
										},
									}}
								>
									<Typography
										variant="subtitle2"
										color="textSecondary"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									>
										Position Value
									</Typography>
									<Typography
										variant="h4"
										color="textPrimary"
										fontFamily="Oxanium"
										fontWeight={500}
										sx={{
											fontSize: {
												xs: '24px',
												sm: '30px',
												md: '34px',
											},
										}}
									>
										{(() => {
											const totalValue = parseFloat(
												userApyValue?.totals?.total?.toString() ||
													'0',
											);

											return totalValue === 0
												? '-'
												: `$${totalValue.toFixed(2)}`;
										})()}
									</Typography>
									<Typography
										variant="body2"
										color="textSecondary"
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '12px',
												sm: '13px',
												md: '14px',
											},
										}}
									>
										Principal + accrued yield
									</Typography>
								</CardContent>
							</StyledCardInner>
						</StatsCard>
					</Grid>
				</Grid>

				<Grid container spacing={{ xs: 1.5, sm: 2 }}>
					<Grid item xs={12} sm={6}>
						<StatsCard>
							<StyledCardInner>
								<CardContent
									sx={{
										padding: { xs: '20px', sm: '24px' },
										paddingBottom: {
											xs: '20px !important',
											sm: '24px !important',
										},
									}}
								>
									<Box
										display="flex"
										flexDirection="column"
										gap={1}
										mb={{ xs: 1.5, sm: 2 }}
									>
										{agentsList.map((agent) => {
											const isSelected =
												agent.id === selectedAgentId;
											return (
												<RiskButton
													disabled={agent?.disabled}
													key={agent.id}
													selected={isSelected}
													fullWidth
													onClick={() =>
														onSelectAgent(agent.id)
													}
													sx={{
														justifyContent:
															'space-between',
														alignItems: 'center',
														backgroundColor:
															isSelected
																? 'var(--atlas-accent-primary-glow-alpha-10)'
																: 'background.paper',
														borderColor: isSelected
															? 'var(--atlas-accent-primary-alt)'
															: 'var(--atlas-border-soft)',
													}}
												>
													<Box
														component="span"
														alignItems="center"
														gap={1}
														textAlign="left"
														sx={{
															display:
																'inline-flex',
														}}
													>
														<Typography
															color="textPrimary"
															variant="h6"
															fontFamily={
																'Oxanium'
															}
															fontWeight={700}
															component="span"
														>
															{agent.name}
														</Typography>
														<Box
															component="span"
															alignItems="center"
															gap={1}
															sx={{
																display:
																	'inline-flex',
															}}
														>
															{agent.details.map(
																(
																	d: AgentDetail,
																) => (
																	<Tooltip
																		key={`${agent.id}-${d.name}`}
																		title={
																			d.name
																		}
																		arrow
																	>
																		<Box
																			component="img"
																			src={
																				d.src
																			}
																			alt={
																				d.name
																			}
																			onClick={(
																				event,
																			) => {
																				event.stopPropagation();
																				window.open(
																					d.link,
																					'_blank',
																					'noopener,noreferrer',
																				);
																			}}
																			sx={{
																				width: 24,
																				height: 24,
																				borderRadius:
																					'50%',
																				objectFit:
																					'cover',
																				border: '1px solid',
																				borderColor:
																					isSelected
																						? 'rgba(255,255,255,0.24)'
																						: 'var(--atlas-border-soft)',
																				cursor: 'pointer',
																			}}
																		/>
																	</Tooltip>
																),
															)}
														</Box>
													</Box>
													<Box textAlign="right">
														<Typography
															color="textPrimary"
															variant="body2"
															fontWeight={600}
														>
															{agent.yield}
														</Typography>
														{isSelected && (
															<Typography
																color="textPrimary"
																variant="caption"
																sx={{
																	fontWeight: 700,
																	letterSpacing:
																		'0.03em',
																}}
															>
																Estimated
															</Typography>
														)}
													</Box>
												</RiskButton>
											);
										})}
									</Box>

									<Typography
										variant="h6"
										gutterBottom
										color="textPrimary"
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '16px',
												sm: '18px',
												md: '20px',
											},
										}}
									>
										Agent Activity
									</Typography>
									<Typography
										color="textSecondary"
										gutterBottom
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '13px',
												sm: '14px',
												md: '16px',
											},
											mb: { xs: 1.5, sm: 2 },
										}}
									>
										Tasks performed by agent
									</Typography>

									<Box>
										{loadingPosition ? (
											<Box textAlign="center" py={4}>
												<CircularProgress size={30} />
												<Typography
													variant="caption"
													display="block"
													mt={2}
												>
													Loading position...
												</Typography>
											</Box>
										) : !positionData?.result ||
										  !Array.isArray(
												positionData.result,
										  ) ? (
											<Box textAlign="center" py={4}>
												<Typography
													variant="body2"
													color="textPrimary"
												>
													{lastPositionId
														? 'Loading position details...'
														: 'No position yet. Make your first deposit to start earning!'}
												</Typography>
												{lastPositionId && (
													<Typography
														variant="caption"
														display="block"
														mt={1}
														color="textPrimary"
													>
														Position{' '}
														{/*#
														{lastPositionId}*/}
													</Typography>
												)}
											</Box>
										) : (
											(() => {
												const [
													,
													,
													credited,
													finalized,
												] = positionData.result;

												const creditedAmount = parseInt(
													String(credited || 0),
												);
												const posId = positionData.id;

												if (vaultBalance === 0) {
													return (
														<Box
															textAlign="center"
															py={4}
														>
															<Typography
																variant="body2"
																color="textPrimary"
															>
																Position
																withdrawn
															</Typography>
														</Box>
													);
												}

												return (
													<Box
														key={posId}
														display="flex"
														justifyContent="space-between"
														alignItems="center"
														p={{ xs: 1.5, sm: 2 }}
														mb={1}
														bgcolor="var(--atlas-bg-app-alt)"
														borderRadius={1}
														borderColor={
															'var(--atlas-border-strong)'
														}
														flexWrap="wrap"
														gap={1}
													>
														<Box
															display="flex"
															alignItems="center"
															gap={2}
															flex={1}
														>
															<Box
																width={{
																	xs: 6,
																	sm: 8,
																}}
																height={{
																	xs: 6,
																	sm: 8,
																}}
																borderRadius="50%"
																bgcolor={
																	finalized
																		? 'success.main'
																		: 'warning.main'
																}
																color="white"
															/>
															<Box>
																{!isAllocated ? (
																	<Typography
																		variant="body2"
																		fontWeight="medium"
																		fontFamily="Oxanium"
																		display={
																			'inline'
																		}
																		color={
																			'var(--atlas-text-primary)'
																		}
																	>
																		Position
																	</Typography>
																) : (
																	<Typography
																		variant="body2"
																		fontWeight="medium"
																		color={
																			'var(--atlas-text-primary)'
																		}
																		fontFamily="Oxanium"
																		display={
																			'inline'
																		}
																	>
																		Allocated
																		in{' '}
																		{isHyperionAgent ? (
																			<>
																				{activeAgent?.details.map(
																					(
																						d,
																					) => (
																						<Typography
																							display="inline"
																							variant="body2"
																							fontWeight="medium"
																							fontFamily="Oxanium"
																						>
																							{d.name ||
																								AGENTS.TappExchange}
																						</Typography>
																					),
																				)}
																			</>
																		) : (
																			<>
																				{activeAgent?.details.map(
																					(
																						d,
																					) => (
																						<Typography
																							display="inline"
																							variant="body2"
																							fontWeight="medium"
																							fontFamily="Oxanium"
																							onClick={() =>
																								window.open(
																									d.link,
																									'_blank',
																								)
																							}
																							sx={{
																								color: 'var(--atlas-accent-primary-alt)',
																								cursor: 'pointer',
																							}}
																						>
																							{d.name ||
																								AGENTS.TappExchange}{' '}
																						</Typography>
																					),
																				)}
																			</>
																		)}
																	</Typography>
																)}

																<Typography
																	variant="caption"
																	color={
																		'var(--atlas-text-subtle-alt)'
																	}
																	fontFamily="Oxanium"
																	display={
																		'block'
																	}
																>
																	{finalized ? (
																		<>
																			<strong
																				style={{
																					color: 'var(--atlas-accent-success)',
																				}}
																			>
																				Finalized
																			</strong>{' '}
																			•
																			Deposited:{' '}
																			{formatUSDC(
																				vaultBalance,
																			)}{' '}
																			USDC
																			{creditedAmount >
																				0 && (
																				<>
																					{' '}
																					→
																					Credited:{' '}
																					{formatUSDC(
																						creditedAmount,
																					)}{' '}
																					USDC
																				</>
																			)}
																		</>
																	) : (
																		<>
																			Deposited:{' '}
																			{formatUSDC(
																				vaultBalance,
																			)}{' '}
																			USDC
																		</>
																	)}
																</Typography>
															</Box>
														</Box>
														{positionSelected && (
															<Button
																variant="text"
																size="small"
																onClick={() =>
																	setActivityDetailOpen(
																		true,
																	)
																}
																sx={{
																	textTransform:
																		'none',
																	fontFamily:
																		'Oxanium',
																	ml: 1,
																	color: 'var(--atlas-accent-primary-alt)',
																	boxShadow:
																		'none',
																	'&:hover': {
																		boxShadow:
																			'none',
																	},
																}}
															>
																Details
															</Button>
														)}
														{/*<Box
															display="flex"
															gap={1}
															alignItems="center"
														>
															<Typography
																variant="body2"
																fontWeight="medium"
																color={
																	'success.main'
																}
																fontFamily="Oxanium"
															>
																{formatUSDC(
																	creditedAmount,
																)}{' '}
																USDC
															</Typography>
														</Box>*/}
													</Box>
												);
											})()
										)}
									</Box>
								</CardContent>
							</StyledCardInner>
						</StatsCard>
					</Grid>

					<Grid item xs={12} sm={6}>
						<StatsCard>
							<StyledCardInner>
								<CardContent
									sx={{
										padding: { xs: '20px', sm: '24px' },
										paddingBottom: {
											xs: '20px !important',
											sm: '24px !important',
										},
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<Typography
										variant="h6"
										gutterBottom
										color="textPrimary"
										fontFamily="Oxanium"
										sx={{
											fontSize: {
												xs: '16px',
												sm: '18px',
												md: '20px',
											},
										}}
									>
										Actions
									</Typography>

									<Box sx={{ height: '100%', flexGrow: 1 }}>
										<Typography
											variant="subtitle2"
											gutterBottom
											fontFamily="Oxanium"
											color="textSecondary"
											sx={{
												fontSize: {
													xs: '14px',
													sm: '15px',
													md: '16px',
												},
											}}
										>
											Manage Your Positions
										</Typography>
										<Alert
											severity="info"
											sx={{
												mb: 2,
												backgroundColor:
													'var(--atlas-bg-surface-interactive)',
												borderColor:
													'var(--atlas-border-accent)',
											}}
										>
											<Typography
												variant="body2"
												fontFamily="Oxanium"
												color="textPrimary"
												sx={{
													fontSize: {
														xs: '12px',
														sm: '13px',
														md: '14px',
													},
												}}
											>
												Active: $
												{balance.toLocaleString()} USDC
												<br />
												PNL: ${' '}
												{(
													parseFloat(
														userApyValue?.totals
															?.total ?? '0',
													) -
													parseFloat(
														userApyValue?.totals
															?.invested ?? '0',
													)
												).toFixed(2) || balance}
												<br />
											</Typography>
										</Alert>

										{/* Withdraw Button for Active Position */}
										{positionData?.result &&
											(() => {
												const [
													,
													,
													credited,
													finalized,
												] = positionData.result;

												const creditedAmount = parseInt(
													String(credited || 0),
												);
												const posId = positionData.id;

												// Only show button if position has balance
												if (vaultBalance === 0)
													return null;

												return (
													<Box mt={2}>
														<WithdrawButton
															posId={posId}
															vaultBalance={
																vaultBalance
															}
															positionData={
																positionData
															}
															credited={
																creditedAmount
															}
															finalized={
																finalized
															}
															apiBaseUrl={
																apiBaseUrl
															}
															vaultConfig={
																vaultConfig
															}
															onSuccess={async () => {
																if (
																	activeAccount
																) {
																	try {
																		const bal =
																			await getUSDCBalance(
																				activeAccount.address as any,
																			);
																		setUsdcBalance(
																			bal,
																		);
																	} catch (error) {
																		/*console.error(
																			'Failed to refetch USDC balance:',
																			error,
																		);*/
																	}
																}
															}}
														/>
													</Box>
												);
											})()}
									</Box>
								</CardContent>
							</StyledCardInner>
						</StatsCard>
					</Grid>
				</Grid>
			</Box>

			<Dialog
				open={activityDetailOpen}
				onClose={() => setActivityDetailOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogContent sx={{ position: 'relative', p: 3 }}>
					<IconButton
						onClick={() => setActivityDetailOpen(false)}
						size="small"
						sx={{ position: 'absolute', right: 8, top: 8 }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
					<Typography variant="h6" mb={2}>
						Details
					</Typography>
					<Box display="grid" gap={2}>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Days Since Deposit
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDays(
									userApyValue?.positions?.[0]
										?.daysSinceDeposit,
								)}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Days Since Allocation
							</Typography>
							<Typography variant="body2" fontWeight={500}>
								{formatDays(
									userApyValue?.positions?.[0]
										?.daysSinceAllocation,
								)}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Unrealized Breakdown
							</Typography>
							{renderBreakdown(
								positionSelected?.unrealizedBreakdown as Record<
									string,
									string
								>,
							)}
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Main>
	);
};
